import { useMemo, useState, type ReactNode } from 'react';
import { Filter, Search, X } from 'lucide-react';

import { ListSortMenu, type ListSortOption } from '@/shared/components/ListSortMenu';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';

import type {
  SafetyIssuesListFilters,
  SafetyIssuesSortKey,
  SafetyIssuesSortState,
} from './listState';
import type { SafetyIssue, SafetyLayout } from './types';

interface SafetyIssuesFiltersProps {
  filters: SafetyIssuesListFilters;
  issues: SafetyIssue[];
  layouts: SafetyLayout[];
  onFiltersChange: (filters: SafetyIssuesListFilters) => void;
  onSortChange: (sort: SafetyIssuesSortState[]) => void;
  sort: SafetyIssuesSortState[];
}

const SORT_OPTIONS: readonly ListSortOption<SafetyIssuesSortKey>[] = [
  { key: 'description', label: 'Issue' },
  { key: 'status', label: 'Status' },
  { key: 'layout', label: 'Area' },
  { key: 'location', label: 'Location' },
  { key: 'trade', label: 'Trade' },
  { key: 'created_by', label: 'Reported by' },
  { key: 'created_at', label: 'Reported' },
];

const EMPTY_FILTERS: SafetyIssuesListFilters = {
  search: '',
  status: 'ALL',
  layoutId: 'ALL',
  locationId: 'ALL',
  tradeId: 'ALL',
};

const sortByName = <T extends { name: string }>(items: T[]) =>
  items.sort((left, right) =>
    left.name.localeCompare(right.name, undefined, { numeric: true, sensitivity: 'base' })
  );

export const SafetyIssuesFilters = ({
  filters,
  issues,
  layouts,
  onFiltersChange,
  onSortChange,
  sort,
}: SafetyIssuesFiltersProps) => {
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const activeFilterCount = [
    filters.search,
    filters.status !== 'ALL',
    filters.layoutId !== 'ALL',
    filters.locationId !== 'ALL',
    filters.tradeId !== 'ALL',
  ].filter(Boolean).length;
  const locationOptions = useMemo(
    () =>
      sortByName(
        Array.from(
          new Map(
            issues.flatMap((issue) =>
              issue.location_detail
                ? [[String(issue.location_detail.id), issue.location_detail] as const]
                : []
            )
          ).values()
        )
      ),
    [issues]
  );
  const tradeOptions = useMemo(
    () =>
      sortByName(
        Array.from(
          new Map(
            issues.flatMap((issue) =>
              issue.trade_detail
                ? [[String(issue.trade_detail.id), issue.trade_detail] as const]
                : []
            )
          ).values()
        )
      ),
    [issues]
  );
  const layoutOptions = useMemo(() => sortByName([...layouts]), [layouts]);
  const update = (patch: Partial<SafetyIssuesListFilters>) =>
    onFiltersChange({ ...filters, ...patch });

  return (
    <div className="space-y-3">
      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <div className="relative min-w-0 flex-1 md:max-w-[320px]">
          <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={filters.search}
            onChange={(event) => update({ search: event.target.value })}
            placeholder="Search safety issues…"
            className="w-full pl-8"
          />
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="hidden gap-2 md:inline-flex"
          onClick={() => setFilterDialogOpen(true)}
        >
          <Filter className="size-4" />
          Filters
          {activeFilterCount > 0 && <Badge variant="secondary">{activeFilterCount}</Badge>}
        </Button>

        <ListSortMenu
          sort={sort}
          options={SORT_OPTIONS}
          onSortChange={onSortChange}
          className="hidden md:inline-flex"
        />

        {activeFilterCount > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="hidden gap-1 md:inline-flex"
            onClick={() => onFiltersChange(EMPTY_FILTERS)}
          >
            <X className="size-4" />
            Clear Filters
          </Button>
        )}
      </div>

      <div className="flex min-w-0 items-center justify-end gap-2 rounded-md border bg-muted/30 px-3 py-2 md:hidden">
        {activeFilterCount > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mr-auto gap-1 px-2.5"
            onClick={() => onFiltersChange(EMPTY_FILTERS)}
          >
            <X className="size-4" />
            Clear
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0 gap-2 px-2.5"
          onClick={() => setFilterDialogOpen(true)}
        >
          <Filter className="size-4" />
          Filters
          {activeFilterCount > 0 && <Badge variant="secondary">{activeFilterCount}</Badge>}
        </Button>
        <ListSortMenu
          sort={sort}
          options={SORT_OPTIONS}
          onSortChange={onSortChange}
          className="shrink-0 px-2.5"
        />
      </div>

      <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
        <DialogContent className="max-w-2xl" aria-describedby="safety-list-filter-description">
          <DialogHeader>
            <DialogTitle>Filters</DialogTitle>
            <DialogDescription id="safety-list-filter-description">
              Refine the safety issues table.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 sm:grid-cols-2">
            <FilterSelect label="Status">
              <Select
                value={filters.status}
                onValueChange={(value) =>
                  update({ status: value as SafetyIssuesListFilters['status'] })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All statuses</SelectItem>
                  <SelectItem value="OPEN">Open</SelectItem>
                  <SelectItem value="RESOLVED">Resolved</SelectItem>
                </SelectContent>
              </Select>
            </FilterSelect>

            <FilterSelect label="Area">
              <Select
                value={filters.layoutId}
                onValueChange={(value) => update({ layoutId: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All areas</SelectItem>
                  {layoutOptions.map((layout) => (
                    <SelectItem key={layout.id} value={String(layout.id)}>
                      {layout.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterSelect>

            <FilterSelect label="Location">
              <Select
                value={filters.locationId}
                onValueChange={(value) => update({ locationId: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All locations</SelectItem>
                  {locationOptions.map((location) => (
                    <SelectItem key={location.id} value={String(location.id)}>
                      {location.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterSelect>

            <FilterSelect label="Trade">
              <Select value={filters.tradeId} onValueChange={(value) => update({ tradeId: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All trades</SelectItem>
                  {tradeOptions.map((trade) => (
                    <SelectItem key={trade.id} value={String(trade.id)}>
                      {trade.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterSelect>
          </div>

          <DialogFooter className="gap-2 sm:space-x-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onFiltersChange(EMPTY_FILTERS)}
              disabled={activeFilterCount === 0}
            >
              Clear All
            </Button>
            <DialogClose asChild>
              <Button type="button">Done</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const FilterSelect = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="space-y-2">
    <Label>{label}</Label>
    {children}
  </div>
);
